import { CheckCircle2, CircleAlert, Clock, X } from 'lucide-react';
import { useEffect, useRef } from 'react';

export interface ToastData {
	id: number;
	type: 'ok' | 'pending' | 'err';
	msg: string;
	exiting: boolean;
}

const AUTO_DISMISS_MS = 6000;
const APPEARANCE = {
	ok: {
		panel: 'border-green-200 bg-white text-green-950',
		icon: 'bg-green-100 text-green-700',
		Icon: CheckCircle2,
	},
	pending: {
		panel: 'border-amber-300 bg-white text-amber-950',
		icon: 'bg-amber-100 text-amber-700',
		Icon: Clock,
	},
	err: {
		panel: 'border-red-300 bg-white text-red-950',
		icon: 'bg-red-100 text-red-700',
		Icon: CircleAlert,
	},
};

function FeedbackToast({
	toast,
	onDismiss,
}: {
	toast: ToastData;
	onDismiss: (id: number) => void;
}) {
	const callbackRef = useRef(onDismiss);
	const dismissedRef = useRef(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => {
		callbackRef.current = onDismiss;
	}, [onDismiss]);
	useEffect(() => {
		if (toast.type === 'err' || toast.exiting || dismissedRef.current) return;
		const id = toast.id;
		timerRef.current = setTimeout(() => {
			timerRef.current = null;
			if (!dismissedRef.current) {
				dismissedRef.current = true;
				callbackRef.current(id);
			}
		}, AUTO_DISMISS_MS);
		return () => {
			if (timerRef.current !== null) clearTimeout(timerRef.current);
		};
	}, [toast.id, toast.type, toast.exiting]);
	const { panel, icon, Icon } = APPEARANCE[toast.type];
	return (
		<div
			role={toast.type === 'err' ? 'alert' : 'status'}
			aria-atomic="true"
			className={`${toast.exiting ? 'toast-exit' : 'toast-enter'} pointer-events-auto flex w-full max-w-[380px] shrink-0 items-start gap-2 rounded-2xl border p-3 shadow-xl ${panel}`}
		>
			<div className={`mt-2 shrink-0 rounded-full p-1 ${icon}`}>
				<Icon aria-hidden="true" className="h-5 w-5" />
			</div>
			<p className="min-w-0 flex-1 py-2 text-sm font-semibold leading-snug whitespace-pre-line">
				{toast.msg}
			</p>
			<button
				type="button"
				aria-label="Chiudi messaggio"
				className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-black/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current active:bg-black/10"
				onClick={() => {
					if (dismissedRef.current) return;
					dismissedRef.current = true;
					if (timerRef.current !== null) clearTimeout(timerRef.current);
					callbackRef.current(toast.id);
				}}
			>
				<X aria-hidden="true" className="h-5 w-5" />
			</button>
		</div>
	);
}

/** Owns the display timeout; the parent removes items after its exit animation. */
export function ToastContainer({
	toasts,
	onDismiss,
}: {
	toasts: ToastData[];
	onDismiss: (id: number) => void;
}) {
	if (!toasts.length) return null;
	return (
		<div
			className="pointer-events-none fixed inset-x-0 z-50 flex max-h-[70dvh] flex-col items-center gap-2 overflow-y-auto px-4"
			style={{ top: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)' }}
		>
			{toasts.map((toast) => (
				<FeedbackToast key={toast.id} toast={toast} onDismiss={onDismiss} />
			))}
		</div>
	);
}
